import { LoadingCard } from '@/components/Base/LoadingCard/LoadingCard'
import LoadingButton from '@/components/Shared/LoadingButton'
import { ActionMode } from '@/enums/scenario-builder.enum'
import { useAppDispatch } from '@/hooks/use-app-dispatch'
import { useAppSelector } from '@/hooks/use-app-selector'
import { TGFPAReportTemplatePayload } from '@/interfaces/gfpaReport.interface'
import { TReportTemplate } from '@/interfaces/scenarioBuilder.interface'
import { createGFPAReportTemplate, getGFPAReportTemplateDetails, updateGFPAReportTemplate } from '@/services/scenarioBuilder.service'
import {
  setActionMode,
  setSelectedReportTemplate,
  toggleIsReloadingReportTemplateGrid
} from '@/states/scenarioBuilder.slice'
import { convertNumericDateToString } from '@/utils/dateUtils'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Snackbar,
  TextField,
  Tooltip,
  Typography
} from '@mui/material'
import React, { useEffect, useState } from 'react'

interface GFPATemplateDetailsProps {
  reportTemplate: TReportTemplate
}

interface IGFPATemplateData {
  name: string
  histSimCheck: boolean
  histSimBeginDate: string
  histSimEndDate: string
}

export const GFPATemplateDetails: React.FC<GFPATemplateDetailsProps> = ({
  reportTemplate,
}) => {
  const dispatch = useAppDispatch()
  const actionMode = useAppSelector((s) => s.scenarioBuilder.actionMode)
  const userInfo = useAppSelector((s) => s.scenarioBuilder.userInfo)
  
  const isViewOnly = actionMode === ActionMode.VIEW
  const isCreateMode = actionMode === ActionMode.CREATE
  const isUpdateMode = actionMode === ActionMode.UPDATE

  // Loading and error states
  const [isLoading, setIsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [successMessage, setSuccessMessage] = useState<string | null>(null)

  // Template data state
  const [templateData, setTemplateData] = useState<IGFPATemplateData>({
    name: reportTemplate.name || '',
    histSimCheck: true,
    histSimBeginDate: '',
    histSimEndDate: '',
  })

  // Track if data has been modified
  const [hasModifications, setHasModifications] = useState(false)
  const [initialData, setInitialData] = useState<IGFPATemplateData>()

  // Initialize form for new template
  const initializeNewTemplate = () => {
    const newTemplateData: IGFPATemplateData = {
      name: reportTemplate.templateName || '',
      histSimCheck: true,
      histSimBeginDate: '',
      histSimEndDate: '',
    };
    
    setTemplateData(newTemplateData);
    setInitialData(newTemplateData);
    setError(null);
    setSuccessMessage(null);
  };

  // Load template details on mount for update mode
  useEffect(() => {
    if (!isCreateMode && reportTemplate.id) {
      loadTemplateDetails();
    } else if (isCreateMode) {
      initializeNewTemplate();
    }
  }, [actionMode, isCreateMode, reportTemplate])

  // Track modifications
  useEffect(() => {
    if (initialData) {
      const modified = JSON.stringify(templateData) !== JSON.stringify(initialData)
      setHasModifications(modified)
    }
  }, [templateData, initialData])

  const loadTemplateDetails = async () => {
    setIsLoading(true)
    try {
      const response = await getGFPAReportTemplateDetails(reportTemplate.id)
      const data = response.data[0]
      
      const beginDate = convertNumericDateToString(data.histSimBeginDate);
      const endDate = convertNumericDateToString(data.histSimEndDate);
      
      // Validate the dates from the API
      if (beginDate || endDate) {
        try {
          const today = new Date();
          today.setHours(0, 0, 0, 0);
          
          if (beginDate) {
            const [beginYear, beginMonth, beginDay] = beginDate.split('-').map(Number);
            const beginDateObj = new Date(beginYear, beginMonth - 1, beginDay);
            
            if (isNaN(beginDateObj.getTime())) {
              throw new Error('Invalid begin date format from API');
            }
        }
          
          if (endDate) {
            const [endYear, endMonth, endDay] = endDate.split('-').map(Number);
            const endDateObj = new Date(endYear, endMonth - 1, endDay);
            
            if (isNaN(endDateObj.getTime())) {
              throw new Error('Invalid end date format from API');
            }
            
            if (endDateObj > today) {
              setError(prev => prev ? `${prev}\nWarning: End date from template is in the future` : 'Warning: End date from template is in the future');
            }
            
            // Validate date range if both dates exist
            if (beginDate) {
              const [beginYear, beginMonth, beginDay] = beginDate.split('-').map(Number);
              const beginDateObj = new Date(beginYear, beginMonth - 1, beginDay);
              
              if (beginDateObj >= endDateObj) {
                setError(prev => prev ? `${prev}\nWarning: Begin date is not before end date in the template` : 'Warning: Begin date is not before end date in the template');
              }
            }
          }
        } catch (error) {
          console.error('Error validating dates from API:', error);
          setError('Warning: Invalid date values in template');
        }
      }
      
      const loadedData: IGFPATemplateData = {
        name: data.name || '',
        histSimCheck: true,
        histSimBeginDate: beginDate,
        histSimEndDate: endDate,
      };
      
      setTemplateData(loadedData);
      setInitialData({ ...loadedData });
    } catch (err) {
      setError('Failed to load template details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (field: keyof IGFPATemplateData) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value
    
    setTemplateData(prev => {
      const newData = {
        ...prev,
        [field]: value
      }
      
      return newData
    })

    // Clear dates when historical template is unchecked
    if (field === 'histSimCheck' && !value) {
      setTemplateData(prev => ({
        ...prev,
        histSimBeginDate: '',
        histSimEndDate: '',
      }))
    }
  }

  const validateForm = (): string | null => {
    // Check if name is provided
    if (!templateData.name?.trim()) {
      return 'Template name is required';
    }

    // Validate dates if either date is provided
    if (templateData.histSimBeginDate || templateData.histSimEndDate) {
      // Check if both dates are provided
      if (!templateData.histSimBeginDate || !templateData.histSimEndDate) {
        return 'Both begin date and end date are required';
      }
      
      try {
        // Parse dates in YYYY-MM-DD format
        const [beginYear, beginMonth, beginDay] = templateData.histSimBeginDate.split('-').map(Number);
        const [endYear, endMonth, endDay] = templateData.histSimEndDate.split('-').map(Number);
        
        const beginDate = new Date(beginYear, beginMonth - 1, beginDay);
        const endDate = new Date(endYear, endMonth - 1, endDay);
        
        // Check if dates are valid
        if (isNaN(beginDate.getTime()) || isNaN(endDate.getTime())) {
          return 'Invalid date format. Please use YYYY-MM-DD format.';
        }
        
        // Check if begin date is before end date
        if (beginDate >= endDate) {
          return 'Begin date must be before end date';
        }
        
        // Check if dates are not in the future
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (beginDate > today) {
          return 'Begin date cannot be in the future';
        }
        
        if (endDate > today) {
          return 'End date cannot be in the future';
        }
        
      } catch (error) {
        console.error('Error validating dates:', error);
        return 'Invalid date values. Please check the dates and try again.';
      }
    }
    
    return null;
  }

  const handleSave = async () => {
    // First validate the form
    const validationError = validateForm();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsSaving(true);
    setError(null);

    try {
      const payload: TGFPAReportTemplatePayload = {
        name: templateData.name,
        reportType: 'GFPAH',
        username: userInfo.name,
        hedgingParam: 'unhedged',
        hedgedValue: 0,
        histSimCheck: templateData.histSimCheck,
        histSimBeginDate: templateData.histSimBeginDate ? parseInt(templateData.histSimBeginDate.replace(/-/g, ''), 10) : 0,
        histSimEndDate: templateData.histSimEndDate ? parseInt(templateData.histSimEndDate.replace(/-/g, ''), 10) : 0
      };

      let response;
      if (isUpdateMode) {
        response = await updateGFPAReportTemplate(reportTemplate.id, payload);
      } else {
        response = await createGFPAReportTemplate(payload);
      }

      if (response?.data?.[0]?.success) {
        // Get the new/updated template details
        const templateId = isUpdateMode ? reportTemplate.id : response.data[0].id;
        const updatedResponse = await getGFPAReportTemplateDetails(templateId);
        
        if (updatedResponse?.data?.[0]) {
          const updatedData = updatedResponse.data[0];
          
          // Update local state with the current template name and new data
          const updatedTemplateData: IGFPATemplateData = {
            name: templateData.name, 
            histSimCheck: true,
            histSimBeginDate: convertNumericDateToString(updatedData.histSimBeginDate) || '',
            histSimEndDate: convertNumericDateToString(updatedData.histSimEndDate) || '',
          };

          setTemplateData(updatedTemplateData);
          setInitialData({ ...updatedTemplateData });
          
          const updatedTemplate = {
            ...updatedData,
            name: templateData.name,
            templateName: templateData.name
          };
          
          dispatch(setSelectedReportTemplate(updatedTemplate));
        }
        
        setSuccessMessage(isUpdateMode ? 'Template updated successfully' : 'Template created successfully');
        dispatch(setActionMode(ActionMode.VIEW));
        dispatch(toggleIsReloadingReportTemplateGrid());
      } else {
        setError(response?.data?.[0]?.message || 'Failed to save template');
      }
    } catch (err: any) {
      let errorMessage = 'An unexpected error occurred';
      
      if (err.response) {
        if (typeof err.response.data === 'string') {
          errorMessage = err.response.data;
        } else if (err.response.data?.message) {
          errorMessage = err.response.data.message;
        } else {
          errorMessage = JSON.stringify(err.response.data);
        }
      } else if (err.message) {
        errorMessage = err.message;
      }
      
      if (errorMessage.includes('Historical Dates are invalid')) {
        errorMessage = 'Please check your date range - dates may be invalid or out of range';
      }
      
      setError(errorMessage);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    dispatch(setActionMode(ActionMode.VIEW))
    dispatch(setSelectedReportTemplate({} as TReportTemplate))
  }

  const getTooltipMessage = () => {
    return ''
  }

  const isSaveDisabled = false

  if (isLoading) {
    return <LoadingCard />
  }

  return (
    <Box>
      {/* Header with title and buttons */}
      <Box
        display='flex'
        justifyContent='space-between'
        py={2}
        boxSizing='border-box'
        data-cy='gfpa-template-container'
      >
        {isViewOnly ? (
          <Typography variant='h4'>
            {reportTemplate.name}
          </Typography>
        ) : (
          <TextField
            label="Historical Scenario Template Name"
            type="text"
            value={templateData.name}
            onChange={handleInputChange('name')}
            size="small"
            data-cy='template-name-input'
          />
        )}
        
        {/* Action buttons */}
        <Box sx={{ display: 'flex', gap: 1 }}>
          {!isViewOnly && (
            <Tooltip title={getTooltipMessage()} arrow placement='top'>
              <span>
                <LoadingButton
                  data-cy='gfpa-save-button'
                  color='primary'
                  variant='contained'
                  onClick={handleSave}
                  disabled={isSaveDisabled}
                  loading={isSaving}
                  size='small'
                >
                  Save
                </LoadingButton>
              </span>
            </Tooltip>
          )}
          <Button
            data-cy='gfpa-cancel-button'
            color='primary'
            onClick={handleCancel}
            size='small'
            disabled={isSaving}
          >
            {isViewOnly ? 'Back' : 'Cancel'}
          </Button>
        </Box>
      </Box>

      {/* Content card */}
      <Card>
        <CardContent>
          <Box sx={{ p: 2 }}>
            {/* Date Input Fields */}
            <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
              <TextField
                label="Begin Date"
                type="date"
                value={templateData.histSimBeginDate}
                onChange={handleInputChange('histSimBeginDate')}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
                required
                data-cy='begin-date-input'
                disabled={isViewOnly}
              />
              <TextField
                label="End Date"
                type="date"
                value={templateData.histSimEndDate}
                onChange={handleInputChange('histSimEndDate')}
                InputLabelProps={{
                  shrink: true,
                }}
                size="small"
                required
                data-cy='end-date-input'
                disabled={isViewOnly}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Success/Error Messages */}
      <Snackbar
        open={!!successMessage}
        autoHideDuration={6000}
        onClose={() => setSuccessMessage(null)}
      >
        <Alert severity="success" onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError(null)}
      >
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  )
}
